/**
 * Wire-progress-ctrl throttle (docs/file-transfer-resume.md §8.2/§9.2 —
 * receiver "progress ctrl (throttled)" row; `@reflecto/core`'s `FileCtrl`
 * "progress" variant comment: `// throttle >=2s`). The RECEIVER is the only
 * role that ever sends a `progress` ctrl over the wire (the sender only
 * displays inbound progress, see `activeSendControl.ts`'s `notifyPeerProgress`).
 * Local (own-side) UI progress via `onProgress`/`onPartWritten` is NEVER
 * throttled — this module gates only the outbound WIRE send.
 *
 * Leading + trailing, last-write-wins, no queue: the first call after a
 * quiet period sends immediately; further calls inside the `throttleMs`
 * window are coalesced into a single pending value (never queued — the
 * LATEST value always wins) and flushed once, at the window boundary, via a
 * trailing timer — so a burst of part-completions never sends more than one
 * ctrl per window, and the burst's final value is never silently dropped.
 *
 * Pure and injectable (`now`) — fake-timer-testable in isolation
 * (`__tests__/progressThrottle.test.ts`). Used ONCE by both
 * `receiveHost.ts` (fresh accept) and `receiveResume.ts` (resume) via
 * `runReceiveLoop`'s `onProgressCtrl` sibling param — not duplicated.
 */

/** Receiver progress-ctrl throttle window (§8.2/§9.2's "≥2s" wire comment). */
export const FT_PROGRESS_THROTTLE_MS = 2_000;

/**
 * Wrap `send` (fire-and-forget — the caller is responsible for its own
 * error handling, e.g. `.catch(() => {})`) so it fires at most once per
 * `throttleMs` window. `now` defaults to `Date.now` (real wall clock);
 * tests inject a fake clock alongside `vi.useFakeTimers()` so the internal
 * trailing `setTimeout` is also fake-controlled.
 */
export function createThrottledProgressSender(
  send: (chunksDone: number) => void,
  throttleMs: number = FT_PROGRESS_THROTTLE_MS,
  now: () => number = Date.now
): (chunksDone: number) => void {
  let lastSentAt = Number.NEGATIVE_INFINITY;
  let pendingValue: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush(value: number): void {
    lastSentAt = now();
    pendingValue = null;
    timer = null;
    send(value);
  }

  return (chunksDone: number) => {
    pendingValue = chunksDone;
    const elapsed = now() - lastSentAt;
    if (elapsed >= throttleMs) {
      flush(chunksDone);
      return;
    }
    if (timer === null) {
      timer = setTimeout(() => {
        if (pendingValue !== null) flush(pendingValue);
      }, throttleMs - elapsed);
    }
  };
}
